import React, { useState, useEffect } from "react";
import { Heart, Search, Users, CheckCircle, AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AdminMatchmaker = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchUser1, setSearchUser1] = useState("");
  const [searchUser2, setSearchUser2] = useState("");
  const [selectedUser1, setSelectedUser1] = useState(null);
  const [selectedUser2, setSelectedUser2] = useState(null);
  const [creating, setCreating] = useState(false);
  const [recentMatches, setRecentMatches] = useState([]);
  const [error, setError] = useState(null);
  const [showDropdown1, setShowDropdown1] = useState(false);
  const [showDropdown2, setShowDropdown2] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      setError(null);
      // Fetch more users to ensure we get everyone (increased from 100 to 500)
      const response = await api.getAdminUsers({ page: 1, limit: 500, search: "" });
      const userList = response.data.users || [];
      setUsers(userList);
      console.log("Loaded users:", userList.length);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || "Unknown error";
      setError("Failed to load users: " + errorMsg);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredUsers1 = () => {
    if (!searchUser1 || searchUser1.length < 1) return [];
    return users.filter(user => 
      user.name?.toLowerCase().includes(searchUser1.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchUser1.toLowerCase())
    ).slice(0, 10);
  };

  const getFilteredUsers2 = () => {
    if (!searchUser2 || searchUser2.length < 1) return [];
    return users.filter(user => 
      (user.name?.toLowerCase().includes(searchUser2.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchUser2.toLowerCase())) &&
      user.id !== selectedUser1?.id
    ).slice(0, 10);
  };

  const createMatch = async () => {
    if (!selectedUser1 || !selectedUser2) {
      toast.error("Please select both users");
      return;
    }

    setCreating(true);
    try {
      const response = await api.post("/admin/matchmaker/create-match", {
        user1_email: selectedUser1.email,
        user2_email: selectedUser2.email
      });

      if (response.data.success) {
        toast.success(`Match created between ${selectedUser1.name} and ${selectedUser2.name}!`);
        setRecentMatches(prev => [{
          user1: selectedUser1,
          user2: selectedUser2,
          createdAt: new Date().toISOString()
        }, ...prev].slice(0, 5));
        
        setSelectedUser1(null);
        setSelectedUser2(null);
        setSearchUser1("");
        setSearchUser2("");
      } else {
        toast.error(response.data.error || "Failed to create match");
      }
    } catch (error) {
      console.error("Failed to create match:", error);
      toast.error(error.response?.data?.detail || "Failed to create match");
    } finally {
      setCreating(false);
    }
  };

  const selectUser1 = (user) => {
    setSelectedUser1(user);
    setSearchUser1("");
    setShowDropdown1(false);
  };

  const selectUser2 = (user) => {
    setSelectedUser2(user);
    setSearchUser2("");
    setShowDropdown2(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#FAB710]" />
        <span className="ml-2">Loading users...</span>
      </div>
    );
  }

  const filteredUsers1 = getFilteredUsers1();
  const filteredUsers2 = getFilteredUsers2();

  return (
    <div className="space-y-6" data-testid="admin-matchmaker">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-3 rounded-xl">
              <Heart className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Matchmaker</h2>
              <p className="text-white/80">{users.length} users available for matching</p>
            </div>
          </div>
          <button
            onClick={fetchUsers}
            className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Match Creator */}
      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-[#1C3A7A]" />
          Create Mutual Match
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          {/* User 1 Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">User 1</label>
            
            {selectedUser1 ? (
              <div className="bg-green-50 border-2 border-green-400 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img 
                    src={selectedUser1.photos?.[0] || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser1.name || 'U')}&background=1C3A7A&color=fff`}
                    alt={selectedUser1.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-green-400"
                  />
                  <div>
                    <p className="font-semibold text-green-800">{selectedUser1.name}</p>
                    <p className="text-sm text-green-600">{selectedUser1.email}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedUser1(null)}
                  className="bg-red-100 hover:bg-red-200 text-red-600 rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold"
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Type to search users..."
                    value={searchUser1}
                    onChange={(e) => {
                      setSearchUser1(e.target.value);
                      setShowDropdown1(true);
                    }}
                    onFocus={() => setShowDropdown1(true)}
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-lg"
                  />
                </div>
                
                {/* Dropdown for User 1 */}
                {showDropdown1 && searchUser1 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                    {filteredUsers1.length > 0 ? (
                      filteredUsers1.map(user => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => selectUser1(user)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-pink-50 text-left border-b last:border-b-0 transition-colors"
                        >
                          <img 
                            src={user.photos?.[0] || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'U')}&background=1C3A7A&color=fff`}
                            alt={user.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="p-4 text-gray-500 text-center">
                        No users found matching "{searchUser1}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User 2 Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">User 2</label>
            
            {selectedUser2 ? (
              <div className="bg-green-50 border-2 border-green-400 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img 
                    src={selectedUser2.photos?.[0] || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser2.name || 'U')}&background=1C3A7A&color=fff`}
                    alt={selectedUser2.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-green-400"
                  />
                  <div>
                    <p className="font-semibold text-green-800">{selectedUser2.name}</p>
                    <p className="text-sm text-green-600">{selectedUser2.email}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedUser2(null)}
                  className="bg-red-100 hover:bg-red-200 text-red-600 rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold"
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Type to search users..."
                    value={searchUser2}
                    onChange={(e) => {
                      setSearchUser2(e.target.value);
                      setShowDropdown2(true);
                    }}
                    onFocus={() => setShowDropdown2(true)}
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-lg"
                  />
                </div>
                
                {/* Dropdown for User 2 */}
                {showDropdown2 && searchUser2 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                    {filteredUsers2.length > 0 ? (
                      filteredUsers2.map(user => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => selectUser2(user)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-pink-50 text-left border-b last:border-b-0 transition-colors"
                        >
                          <img 
                            src={user.photos?.[0] || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'U')}&background=1C3A7A&color=fff`}
                            alt={user.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="p-4 text-gray-500 text-center">
                        No users found matching "{searchUser2}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Create Match Button - Always visible */}
        <div className="mt-8 text-center">
          {selectedUser1 && selectedUser2 ? (
            <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-6 mb-4">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="text-center">
                  <img 
                    src={selectedUser1.photos?.[0] || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser1.name || 'U')}&background=1C3A7A&color=fff`}
                    alt={selectedUser1.name}
                    className="w-16 h-16 rounded-full object-cover mx-auto border-4 border-white shadow-lg"
                  />
                  <p className="mt-2 font-medium">{selectedUser1.name}</p>
                </div>
                <Heart className="w-8 h-8 text-rose-500 animate-pulse" />
                <div className="text-center">
                  <img 
                    src={selectedUser2.photos?.[0] || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser2.name || 'U')}&background=1C3A7A&color=fff`}
                    alt={selectedUser2.name}
                    className="w-16 h-16 rounded-full object-cover mx-auto border-4 border-white shadow-lg"
                  />
                  <p className="mt-2 font-medium">{selectedUser2.name}</p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 mb-4">Select both users above to create a match</p>
          )}
          
          <button
            onClick={createMatch}
            disabled={creating || !selectedUser1 || !selectedUser2}
            className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all flex items-center gap-2 mx-auto ${
              selectedUser1 && selectedUser2
                ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:from-pink-600 hover:to-rose-600 shadow-lg"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {creating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creating Match...
              </>
            ) : (
              <>
                <Heart className="w-5 h-5" />
                Create Mutual Match
              </>
            )}
          </button>
        </div>
      </div>

      {/* Recent Matches */}
      {recentMatches.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            Recently Created Matches
          </h3>
          <div className="space-y-3">
            {recentMatches.map((match, index) => (
              <div key={index} className="flex items-center justify-between bg-green-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <img 
                    src={match.user1.photos?.[0] || `https://ui-avatars.com/api/?name=${encodeURIComponent(match.user1.name || 'U')}&background=1C3A7A&color=fff`}
                    alt={match.user1.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <Heart className="w-4 h-4 text-rose-500" />
                  <img 
                    src={match.user2.photos?.[0] || `https://ui-avatars.com/api/?name=${encodeURIComponent(match.user2.name || 'U')}&background=1C3A7A&color=fff`}
                    alt={match.user2.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <span className="text-sm">
                    <span className="font-medium">{match.user1.name}</span>
                    {" & "}
                    <span className="font-medium">{match.user2.name}</span>
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(match.createdAt).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h4 className="font-medium text-blue-800 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          How it works
        </h4>
        <ul className="mt-2 text-sm text-blue-700 space-y-1">
          <li>• Type a name or email in the search boxes above</li>
          <li>• Click on a user from the dropdown to select them</li>
          <li>• Select both User 1 and User 2</li>
          <li>• Click "Create Mutual Match" button</li>
          <li>• Both users will see each other in their Connections tab</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminMatchmaker;
